import * as React from 'react';

import { theme } from '@/lib/ui/theme';
import { cn } from '@/lib/utils';

import { Skeleton } from './skeleton';

interface CardProps extends React.ComponentProps<'div'> {
  isLoading?: boolean;
  unstyled?: boolean;
}

function Card({
  className,
  isLoading,
  unstyled,
  children,
  ...props
}: CardProps) {
  if (unstyled) return <>{children}</>;
  if (isLoading) {
    return (
      <div
        data-slot="card"
        className={cn('glass-card animate-pulse rounded-xl p-6', className)}
        {...props}
      >
        <Skeleton className="mb-4 h-6 w-24" />
        <Skeleton className="mb-2 h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    );
  }
  return (
    <div
      data-slot="card"
      className={cn('glass-card rounded-xl p-6', className)}
      {...props}
    >
      {children}
    </div>
  );
}

function CardHeader({
  className,
  unstyled,
  children,
  ...props
}: React.ComponentProps<'div'> & { unstyled?: boolean }) {
  if (unstyled) return <>{children}</>;
  return (
    <div
      data-slot="card-header"
      className={cn(
        '@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-title"
      className={cn('text-lg leading-none font-semibold', className)}
      style={{ fontFamily: theme.fonts.body }}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        'col-start-2 row-span-2 row-start-1 self-start justify-self-end',
        className
      )}
      {...props}
    />
  );
}

function CardContent({
  className,
  unstyled,
  children,
  ...props
}: React.ComponentProps<'div'> & { unstyled?: boolean }) {
  if (unstyled) return <>{children}</>;
  return (
    <div data-slot="card-content" className={cn('px-6', className)} {...props}>
      {children}
    </div>
  );
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn('flex items-center px-6 [.border-t]:pt-6', className)}
      {...props}
    />
  );
}

export {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
};
